import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TabsComponent } from '../../../shared/components/tabs/tabs.component';
import { TabPaneComponent } from '../../../shared/components/tabs/tab-pane.component';
import {
  CatalogService,
  CatalogProductSpecification,
  CatalogPriceModel, CatalogPriceFixedPriceModel
} from '../../../core/services/catalog/catalog.service';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { InventoryService, FranchiseResultModel } from '../../../core/services/inventory/inventory.service';
import {StockKeepingUnitSpecifications} from '../../../core/models/catalog/stock-keeping-unit.model';
import {TradePolicyModel} from '../../../core/models/catalog/trade-policy.model';
import {ROLE_VTEX_CATALOG_READ, ROLE_SUPER_ADMINISTRATOR, ROLE_VTEX_ORDERS_WRITE } from '../../../core/models/user/user-info.model';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {ProductViewData} from './admin-product-view.model';
import {ProductDetailsSectionComponent} from './components/product-details-section/product-details-section.component';
import {ProductSpecificationsComponent} from './components/product-specifications/product-specifications.component';
import {ProductTradePoliciesComponent} from './components/product-trade-policies/product-trade-policies.component';
import {SkuDetailsCardComponent} from './components/sku-details-card/sku-details-card.component';
import {SkuPricesComponent} from './components/sku-prices/sku-prices.component';
import {SkuSimulationComponent} from './components/sku-simulation/sku-simulation.component';
import {SkuInventoryComponent} from './components/sku-inventory/sku-inventory.component';
import {InventoryHistoryVarLComponent} from './components/sku-inventory-history/variation-l.component';

@Component({
  selector: 'app-admin-product-view',
  standalone: true,
  imports: [
    CommonModule,
    TabsComponent,
    TabPaneComponent,
    AlertComponent,
    LoadingComponent,
    ProductDetailsSectionComponent,
    ProductSpecificationsComponent,
    ProductTradePoliciesComponent,
    SkuDetailsCardComponent,
    SkuPricesComponent,
    SkuSimulationComponent,
    SkuInventoryComponent,
    InventoryHistoryVarLComponent,
  ],
  templateUrl: './admin-product-view.component.html',
  styleUrl: './admin-product-view.component.scss'
})
export class AdminProductViewComponent implements OnInit {
  productId?: number;
  loading = false;
  error: string | null = null;
  version: 'v1' | 'v2' = 'v1';
  isAdmin = false;

  product: ProductViewData | null = null;

  franchises: FranchiseResultModel[] = [];
  loadingAccounts = false;

  tradePolicies: TradePolicyModel[] = [];

  productSpecifications: Map<string, string[]> = new Map<string, string[]>();
  skuSpecifications: Map<number, Map<string, string[]>> = new Map<number, Map<string, string[]>>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly catalog: CatalogService,
    private readonly inventory: InventoryService,
    private readonly auth: AuthenticationService,
  ) {}

  ngOnInit(): void {
    this.productId = Number(this.route.snapshot.paramMap.get('productId'));
    if (!Number.isFinite(this.productId) || (this.productId || 0) <= 0) {
      this.error = 'ProductId inválido na rota.';
      return;
    }
    this.fetchProduct(this.productId);
    this.loadTradePolicies();
    this.loadFranchises();
    this.version = this.auth.hasRole(ROLE_VTEX_CATALOG_READ) ? 'v1' : 'v2';
    this.isAdmin = this.auth.hasRole(ROLE_VTEX_ORDERS_WRITE); // gambiarra consciente
  }

  private loadTradePolicies(): void {
    this.tradePolicies = [];
    this.catalog.getTradePolicies().subscribe({
      next: (res) => {
        this.tradePolicies = res;
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  private fetchProduct(id: number): void {
    this.loading = true;
    this.catalog.find(id).subscribe({
      next: (res) => {
        console.log(res);
        this.product = {
          ...res,
          skus: res.skus.map(x => {
            return {
              ...x,
              skuPricesLoading: true,
              skuPrices: <CatalogPriceModel>{},
            };
          })
        };

        this.productSpecifications = this.getProductSpecification(this.product.specifications ?? []);
        for (const sku of this.product.skus || []) {
          this.skuSpecifications.set(sku.id, this.getSpecification(sku.skuSpecifications ?? []));
        }
        this.loading = false;

        this.fetchSkuPrices();
      },
      error: (err) => {
        console.error(err);
        this.error = 'Falha ao carregar o produto.';
        this.loading = false;
      }
    });
  }

  private fetchSkuPrices(): void {
    if (this.isMarketplaceProduct() && this.productId) {
      this.catalog.getOffers(this.productId).subscribe({
        next: (value) => {
          const productOffer = value.find(x => Number(x.productId) === this.productId);
          this.product?.skus.map(sku => {
            const offer = productOffer?.skus.find(x => Number(x.id) === sku.id);
            if (offer) {
              sku.skuPrices = {
                itemId: offer.id,
                listPrice: 0,
                costPrice: 0,
                basePrice: 0,
                markup: 0,
                fixedPrices: offer.sellers.map(seller => {
                  const result: CatalogPriceFixedPriceModel = {
                    listPrice: seller.price,
                    value: seller.price,
                    minQuantity: 1,
                    tradePolicyId: seller.id,
                    dateRange: undefined
                  };
                  return result;
                })
              };
            }
          });
        },
        error: err => {
          console.error(err);
        }
      });
      return;
    }
    this.product?.skus.map(async sku => {
      this.catalog.getPrices(sku.id).subscribe({
        next: (res) => {
          sku.skuPrices = res;
          sku.skuPricesLoading = false;
        },
        error: (err) => {
          console.error(err);
          sku.skuPrices = null;
          sku.skuPricesLoading = false;
        }
      });
    });
  }

  private loadFranchises(): void {
    this.loadingAccounts = true;
    this.inventory.listFranchises().subscribe({
      next: (data) => {
        this.franchises = data || [];
        this.loadingAccounts = false;
      },
      error: (err) => {
        console.error(err);
        this.loadingAccounts = false;
      }
    });
  }

  isMarketplaceProduct(): boolean {
    return this.product?.isMarketplace || false;
  }

  private getProductSpecification(specs: CatalogProductSpecification[]): Map<string, string[]> {
    const groupedSpecs = new Map<string, string[]>();
    for (const spec of specs) {
      const values = groupedSpecs.get(spec.mainFieldName) || [];
      values.push(spec.productFieldValueText);
      groupedSpecs.set(spec.mainFieldName, values);
    }
    return groupedSpecs;
  }

  private getSpecification(specs: StockKeepingUnitSpecifications[]): Map<string, string[]> {
    const groupedSpecs = new Map<string, string[]>();
    for (const spec of specs) {
      groupedSpecs.set(spec.fieldName, spec.fieldValues);
    }
    return groupedSpecs;
  }
}
